import { type ReactElement } from 'react';
import { buildTestingEntityMetricColgroup } from '@/features/shared/components/tableSystem/TableV2MetricColumnLayout';
import {
  tableV2ActionsColumnWidth,
  tableV2ChevronColumnWidth,
  type TableV2SkeletonColumnKind,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import type { MetricThresholdGroupKey } from '@/shared/utils/metricsThresholds';

const suiteBaseSkeletonColumns = [
  'chevron',
  'name',
  'status',
  'dateTime',
  'tags',
  'actions',
] satisfies TableV2SkeletonColumnKind[];

export const buildSuiteSkeletonColumns = (
  visibleMetricKeys: MetricThresholdGroupKey[],
  includeProject = false,
): TableV2SkeletonColumnKind[] => [
  ...suiteBaseSkeletonColumns.slice(0, 4),
  ...visibleMetricKeys.map(() => 'metric' as const),
  ...suiteBaseSkeletonColumns.slice(4, 5),
  ...(includeProject ? (['project'] satisfies TableV2SkeletonColumnKind[]) : []),
  ...suiteBaseSkeletonColumns.slice(5),
];

export const suiteSkeletonColumns = buildSuiteSkeletonColumns([
  'passRate',
  'frequency',
  'avgDuration',
  'flakeScore',
]);

export const suiteRunSkeletonColumns = [
  'chevron',
  'name',
  'status',
  'passRate',
  'duration',
  'dateTime',
  'tags',
  'actions',
] satisfies TableV2SkeletonColumnKind[];

export const testRunSkeletonColumns = [
  'name',
  'status',
  'duration',
  'dateTime',
  'tags',
  'actions',
] satisfies TableV2SkeletonColumnKind[];

export const buildSuiteColgroup = (
  visibleMetricKeys: MetricThresholdGroupKey[],
  includeProject = false,
): ReactElement => buildTestingEntityMetricColgroup(visibleMetricKeys, includeProject);

export const suiteColgroup = buildSuiteColgroup([
  'passRate',
  'frequency',
  'avgDuration',
  'flakeScore',
]);

export const suiteRunColgroup = (
  <colgroup>
    <col width={tableV2ChevronColumnWidth} />
    <col width="24%" />
    <col width="10%" />
    <col width="16%" />
    <col width="12%" />
    <col width="16%" />
    <col width="16%" />
    <col width={tableV2ActionsColumnWidth} />
  </colgroup>
) satisfies ReactElement;

export const testRunColgroup = (
  <colgroup>
    <col width="30%" />
    <col width="13%" />
    <col width="13%" />
    <col width="20%" />
    <col width="20%" />
    <col width={tableV2ActionsColumnWidth} />
  </colgroup>
) satisfies ReactElement;
